import {
    AfterViewChecked,
    ChangeDetectorRef,
    DestroyRef,
    Directive,
    HostBinding,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    TemplateRef,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BetBuilderProvider } from '@cds/betting-offer/add-ons';
import { TagType } from '@cds/betting-offer/tags';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { UrlParam } from '@frontend/sports/navigation/core/feature/url-builder';
import { MarketUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';
import { Store } from '@ngrx/store';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import { BehaviorSubject, NEVER, Observable, Subject, distinctUntilChanged, filter, switchMap, timer } from 'rxjs';

import { NavigationRouterService } from '../../../../../lib/navigation/navigation-router.service';
import { IBetslipMessages } from '../../../../base/models/betslip-messages';
import { IBetslipMessagesProviderService } from '../../../../base/services/betslip-messages-provider.service';
import { selectIsLinearBetslip } from '../../../../base/store/selectors';
import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipBetBuilderPick } from '../../../../core/picks/betslip-bet-builder-pick';
import { BetslipGroupPick } from '../../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../../core/picks/betslip-v1-pick';
import { BetslipV2Pick } from '../../../../core/picks/betslip-v2-pick';
import { BetslipV2WinParticipantPick } from '../../../../core/picks/betslip-v2-win-participant-pick';
import { PickOddsState } from '../../../../core/picks/pick-models';
import { lineSwitcherSwapPickStateSelector } from '../../../line-switcher/selector';
import { ILineSwitcherSwapPickState } from '../../../line-switcher/state';
import { EntainUiBetBuilderPickRemovalTracking } from '../../../tracking/tracking.models';
import BetslipPickActions from '../../actions';
import { isPickEachWayCapable, isRacePick } from '../../services/betslip-pick-helpers';
import BetslipPickUtilsService from '../../services/betslip-pick-utils.service';
import PickTypeSelectorService from '../../services/pick-type-selector.service';

@Directive()
export default abstract class BaseBetTypePickComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
    private lineSwitcherSwapPickState$: Observable<ILineSwitcherSwapPickState>;

    @HostBinding('class.bs-digital-pick--locked') get locked(): boolean {
        return !!this.pick && this.pick.oddsState === PickOddsState.Locked;
    }

    @HostBinding('class.bs-digital-pick--open') get open(): boolean {
        return !!this.pick && this.pick.oddsState === PickOddsState.Open;
    }

    @HostBinding('class.highlighted') get highlighted(): boolean {
        return !!this.pick && this.hasHighlight;
    }

    @HostBinding('class.bs-digital-pick--closed') get closed(): boolean {
        return !!this.pick && this.pick.oddsState === PickOddsState.Closed;
    }

    @HostBinding('class.bs-digital-pick--with-pick-actions') get hasPickActions(): boolean {
        return isPickEachWayCapable(this.pick) || (BetslipV2WinParticipantPick.isPick(this.pick) && this.pick.isStartingPriceAvailable());
    }

    @HostBinding('class.has-each-way') get hasEachwayAction(): boolean {
        return isPickEachWayCapable(this.pick);
    }

    pick: BetslipPick;
    pickTemplate: TemplateRef<any>;
    pickClass: string;
    messages: IBetslipMessages;
    hasHighlight = false;
    removeHighlightTimeout = 4000;

    protected pickChangeSubject = new BehaviorSubject<BetslipPick | undefined>(undefined);
    pickChange$: Observable<BetslipPick> = this.pickChangeSubject.asObservable().pipe(filter(isDefined));
    isLinear$: Observable<boolean>;

    protected startRemoveTimer$ = new Subject<boolean>();

    private readonly rxStrategy = inject(RxStrategyProvider);
    private ngZone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);

    protected constructor(
        protected store: Store<IBetslipRootState>,
        private pickTypeSelector: PickTypeSelectorService,
        messageProvider: IBetslipMessagesProviderService,
        public navigationRouter: NavigationRouterService,
        public redirectHelper: RedirectHelperService,
        private changeDetectorRef: ChangeDetectorRef,
        private betslipPickUtilsService: BetslipPickUtilsService,
    ) {
        this.messages = messageProvider.getMessages();
        this.lineSwitcherSwapPickState$ = this.store.select(lineSwitcherSwapPickStateSelector);
        this.isLinear$ = this.store.select(selectIsLinearBetslip);
    }

    ngOnInit(): void {
        this.startRemoveTimer$
            .pipe(
                distinctUntilChanged(),
                switchMap((startTimer) => (startTimer ? timer(this.removeHighlightTimeout) : NEVER)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.removeHighlight());
        this.lineSwitcherSwapPickState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((swapState) => {
            this.hasHighlight = !!swapState.newPickId && swapState.newPickId.isEqual(this.pick.id);
            this.startRemoveTimer$.next(this.hasHighlight);
        });
    }

    ngAfterViewChecked(): void {
        this.startRemoveTimer$.next(this.hasHighlight);
    }

    removeHighlight(): void {
        this.hasHighlight = false;
        this.changeDetectorRef.markForCheck();
    }

    ngOnDestroy(): void {
        this.pickChangeSubject.complete();
    }

    ngOnChanges(changes: ISimpleChanges<BaseBetTypePickComponent>): void {
        if (changes.pick?.currentValue) {
            const [pickTemplate, pickClass] = this.pickTypeSelector.resolve(changes.pick.currentValue);
            this.pickTemplate = pickTemplate;
            this.pickClass = pickClass;
            this.pickChangeSubject.next(changes.pick.currentValue);
        }
    }

    removePick(): void {
        this.rxStrategy
            .schedule(
                () => {
                    if (BetslipBetBuilderPick.isEntainUiBetBuilderPick(this.pick)) {
                        //On click of clearAll of SGP in betslip
                        this.betslipPickUtilsService.trackEntainUiBetBuilderPickRemoval(
                            EntainUiBetBuilderPickRemovalTracking.TrackingAllPickRemovalSgp,
                        );
                    } else {
                        this.betslipPickUtilsService.trackNonSgpPickRemoval(EntainUiBetBuilderPickRemovalTracking.TrackingSinglePickRemoval);
                    }

                    this.store.dispatch(BetslipPickActions.requestRemovePick({ pickId: this.pick.id }));
                },
                { scope: this, patchZone: this.ngZone },
            )
            .subscribe();
    }

    eventClick(): void {
        if (this.pick.oddsState === PickOddsState.Closed || (this.pick.oddsState === PickOddsState.Locked && this.pick.isVirtual)) {
            return;
        }

        this.navigationRouter.resetNavigation();

        if (BetslipBetBuilderPick.isEntainUiBetBuilderPick(this.pick)) {
            return this.redirectHelper.goToEvent(
                {
                    id: this.pick.eventId,
                    name: this.pick.eventName.name,
                },
                MarketUrlParam.BetBuilder,
            );
        }

        /**
         * Case for pre-created betbuilder pick: it has to use `event.event.id` instead of `eventId`
         * because eventId is actually pointing to TV2 betbuilder-only fixture instead of actual event
         */
        if (BetslipBetBuilderPick.isPick(this.pick) && !this.pick.sportcastOptions?.length) {
            return this.redirectHelper.goToEvent({ id: this.pick.event.event.id, name: this.pick.eventName.name });
        }

        if (BetslipV2Pick.isPick(this.pick)) {
            if (isRacePick(this.pick)) {
                return this.redirectHelper.goToFixtureByFixture({
                    ...this.pick.fixture,
                    id: this.pick.fixture.fixtureId,
                    name: { value: this.pick.fixture.name.name, sign: this.pick.fixture.name.signature },
                    meeting: {
                        id: this.pick.fixture.league!.id,
                        name: {
                            value: this.pick.fixture.league!.name.name,
                            sign: this.pick.fixture.league!.name.signature,
                        },
                        type: TagType.Meeting,
                    },
                    fixtureType: this.pick.fixture.fixtureType,
                    sport: {
                        id: this.pick.fixture.sportId,
                        name: { value: this.pick.fixture.sportName.name, sign: this.pick.fixture.sportName.signature },
                        type: TagType.Sport,
                        isEsport: false,
                    },
                });
            }
            const fixture = {
                id: this.pick.fixture.linkedEventIds.length ? this.pick.fixture.linkedEventIds[0].toString() : this.pick.fixture.fixtureId,
                name: this.pick.eventName.name,
            };

            if (this.pick.isVirtual) {
                const sport = { id: this.pick.sportId, name: this.pick.sportName.name };
                const competition = { id: this.pick.leagueId, name: this.pick.leagueName.name };

                return this.redirectHelper.goToVirtualSport(sport, competition, fixture.id);
            }

            this.redirectHelper.goToEvent(fixture);
        }

        if (BetslipV1Pick.isPick(this.pick)) {
            const fixture = { id: this.pick.event.id, name: this.pick.eventName.name };

            if (this.pick.isVirtual) {
                const sport = { id: this.pick.sportId, name: this.pick.sport.name.name };
                const competition = { id: this.pick.league.id, name: this.pick.league.name.name };

                return this.redirectHelper.goToVirtualSport(sport, competition, fixture.id);
            }

            this.redirectHelper.goToEvent(fixture);
        }

        if (BetslipGroupPick.isPick(this.pick)) {
            let fixtureParam: UrlParam | null = null;
            for (const leg of this.pick.getLegs()) {
                if (BetslipV1Pick.isPick(leg)) {
                    fixtureParam = { id: leg.event.id, name: leg.eventName.name };
                    break;
                }
                if (BetslipV2Pick.isPick(leg)) {
                    if (leg.fixture.linkedEventIds.length) {
                        fixtureParam = { id: leg.fixture.linkedEventIds[0].toString(), name: leg.eventName.name };
                        break;
                    }

                    if (this.pick.groupInfo.provider === BetBuilderProvider.BybFootball) {
                        fixtureParam = { id: leg.fixture.fixtureId, name: leg.eventName.name };
                        break;
                    }
                }
            }
            if (fixtureParam) {
                this.redirectHelper.goToEvent(fixtureParam);
            }
        }
    }
}
